import { useRef, useState, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { SendIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import { Button } from '../../../ui/Button/Button';

/**
 * Auto-resizing textarea + Send button.
 *
 * Enter sends; Shift+Enter inserts a newline. The composer is
 * disabled (with a soft visual cue) while the assistant is
 * streaming or while a confirmation is pending — both block new
 * user turns. The disabled state stays mounted so the user can
 * still see what they typed and the panel doesn't shift.
 */
export interface ComposerProps {
  disabled?: boolean;
  onSubmit: (text: string) => void;
}

export const Composer = ({ disabled, onSubmit }: ComposerProps) => {
  const { t } = useTranslation('m-agent');
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSubmit(trimmed);
    setValue('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className='border-t border-slate-200 bg-white px-3 py-3'>
      <div
        className={cn(
          'flex items-end gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 transition-colors focus-within:border-indigo-300 focus-within:bg-white focus-within:ring-2 focus-within:ring-indigo-100',
          disabled && 'opacity-60',
        )}
      >
        <textarea
          ref={textareaRef}
          rows={1}
          value={value}
          onChange={e => {
            setValue(e.target.value);
            const ta = e.target;
            ta.style.height = 'auto';
            ta.style.height = `${Math.min(ta.scrollHeight, 160)}px`;
          }}
          onKeyDown={handleKeyDown}
          placeholder={t(
            'composer.placeholder',
            'Ask anything — orders, clients, drivers, routes…',
          )}
          disabled={disabled}
          className='max-h-40 min-h-[36px] flex-1 resize-none bg-transparent text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none disabled:cursor-not-allowed'
        />
        <Button
          type='button'
          size='icon'
          onClick={submit}
          disabled={disabled || !value.trim()}
          className='size-9 flex-none rounded-full bg-gradient-to-br from-indigo-500 via-violet-500 to-fuchsia-500 text-white shadow-md shadow-indigo-500/30 hover:from-indigo-600 hover:via-violet-600 hover:to-fuchsia-600 disabled:from-slate-300 disabled:via-slate-300 disabled:to-slate-300 disabled:shadow-none'
        >
          <SendIcon className='size-4' />
        </Button>
      </div>
      <p className='mt-1.5 text-[11px] text-slate-400'>
        {t('composer.hint', 'Press Enter to send · Shift+Enter for newline')}
      </p>
    </div>
  );
};
